import {getImportCheckpoint,saveImportCheckpoint} from './importTaskStorage';
import {ECOMMERCE_API_BASE_URL} from '../config';
import {teamFetch} from './teamTransport';
import {readSessionKey} from './queryCache';
import {apiErrorMessage,readErrorBody} from './errorMessage';

export type ImportTransferProgress={done:number;total:number;stage:'uploading'|'processing'};
type Upload={scope:string;ticket:string;offset:number;chunkSize:number;started:number;pending?:Promise<string>};
const uploads=new WeakMap<File,Upload>();
const states=new WeakMap<File,ImportTransferProgress>();
const listeners=new Set<(file:File,state:ImportTransferProgress)=>void>();
export function watchImportUpload(file:File,listener:(state:ImportTransferProgress)=>void) {
    const update=(changed:File,state:ImportTransferProgress)=>{if(changed===file)listener(state);};
    listeners.add(update);const state=states.get(file);if(state)listener(state);
    return ()=>{listeners.delete(update);};
}
const emit=(file:File,state:ImportTransferProgress)=>{states.set(file,state);listeners.forEach(listener=>listener(file,state));};
const base=`${ECOMMERCE_API_BASE_URL}/erp/import-uploads`;
const send=(url:string,init:RequestInit)=>teamFetch(url,init,()=>new Error('网络中断，请重试续传'));
async function checked(response:Response) {
    if(!response.ok)throw Object.assign(new Error(apiErrorMessage(response.status,await readErrorBody(response))),{status:response.status});
    return response.json();
}

async function stage(file:File,signal?:AbortSignal):Promise<string> {
    const scope=readSessionKey();
    const check=()=>{if(signal?.aborted||scope!==readSessionKey())throw new DOMException('Session changed or cancelled','AbortError');};
    let state=uploads.get(file);
    if(!state){const checkpoint=getImportCheckpoint(file);if(checkpoint){state={...checkpoint,scope};uploads.set(file,state);}}
    if(state?.scope!==scope||Date.now()-state.started>23*3600_000)state=undefined;
    if(state?.pending)return state.pending;
    if(!state){
        check();emit(file,{done:0,total:file.size,stage:'uploading'});
        const result=await checked(await send(base,{method:'POST',signal,headers:{'Content-Type':'application/json'},body:JSON.stringify({name:file.name,size:file.size})}));
        check();state={scope,ticket:result.ticket,offset:0,chunkSize:result.chunk_size,started:Date.now()};uploads.set(file,state);await saveImportCheckpoint(file,state);
    }
    const current=state;
    const work=async()=>{
        while(current.offset<file.size){
            check();const offset=current.offset;
            const chunk=file.slice(offset,offset+current.chunkSize);
            let received:any;
            // Only immutable byte chunks are replayed. Business writes never are.
            for(let attempt=0;;attempt++){
                check();
                const controller=new AbortController();
                const cancel=()=>controller.abort();signal?.addEventListener('abort',cancel,{once:true});
                const timer=setTimeout(cancel,60_000);
                try{
                    received=await checked(await send(`${base}/chunk?offset=${offset}`,{method:'POST',body:chunk,signal:controller.signal,
                        headers:{'Content-Type':'application/octet-stream','X-Upload-Ticket':current.ticket}}));
                    break;
                }catch(error){
                    check();const status=(error as {status?:number}).status;
                    if(attempt>=2||(status&&status<500&&status!==408&&status!==429))throw error;
                    await new Promise(resolve=>setTimeout(resolve,500*(attempt+1)));
                }finally{clearTimeout(timer);signal?.removeEventListener('abort',cancel);}
            }
            check();
            if(received.offset!==offset+chunk.size)throw new Error('上传进度不一致，请重新选择文件');
            current.offset=received.offset;
            await saveImportCheckpoint(file,current);
            emit(file,{done:current.offset,total:file.size,stage:'uploading'});
        }
        check();emit(file,{done:file.size,total:file.size,stage:'processing'});
        return current.ticket;
    };
    current.pending=work();
    try{return await current.pending;}finally{current.pending=undefined;}
}

/** Shared Excel/CSV intake transport. File identity retains acknowledged chunks. */
export async function prepareImportForm(url:string,form:FormData,signal?:AbortSignal):Promise<FormData> {
    if(!/\/(?:product-batch\/(?:preview|import)|inventory\/stock-updates\/(?:preview|commit)|livestreams\/\d+\/(?:order-imports|plan-imports)(?:\/(?:preview|commit))?|products\/imports\/preview)(?:\?|$)/.test(url))return form;
    const file=form.get('file');
    if(!(file instanceof File)||!file.size)return form;
    const ticket=await stage(file,signal);const result=new FormData();
    form.forEach((value,key)=>{if(key!=='file')result.append(key,value);});
    result.set('file_upload_ticket',ticket);return result;
}
